import { useEffect, useState } from "react";
import useInView from "../hooks/useInView";

/**
 * CountUp — animate a numeric stat from 0 to `to` once the element
 * scrolls into view. Uses easeOutCubic for a satisfying deceleration.
 *
 * Props:
 *   to:        target number (required)
 *   duration:  ms, default 1500
 *   prefix:    string before number (e.g., "$")
 *   suffix:    string after number  (e.g., "+", "%", "B")
 *   decimals:  digits after decimal (default 0)
 *   className: pass-through
 */
const CountUp = ({
  to,
  duration = 1500,
  prefix    = "",
  suffix    = "",
  decimals  = 0,
  className = "",
  "data-testid": dataTestId,
}) => {
  const [ref, inView] = useInView({ threshold: 0.4 });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;

    // Reduced-motion: snap to final value
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setVal(to);
      return;
    }

    let rafId;
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(to * eased);
      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setVal(to);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [inView, to, duration]);

  const formatted = val.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <span ref={ref} className={className} data-testid={dataTestId}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

export default CountUp;
